import React, { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
const QR_OPTS = {
  errorCorrectionLevel: 'M',
  margin: 2,
  width: 220,
  color: { dark: '#0a1628', light: '#ffffff' },
};

async function parseApiError(res) {
  try {
    const data = await res.json();
    return data?.error || data?.message || `Request failed (${res.status})`;
  } catch {
    return `Request failed (${res.status})`;
  }
}

/**
 * Admin Security settings — Setup / confirm TOTP 2FA (Google Authenticator).
 * Security-only UI; does not touch payments or other business flows.
 */
export default function AdminTotpSetupPanel({ authToken }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [verifiedAt, setVerifiedAt] = useState(null);
  const [secret, setSecret] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [setupStarted, setSetupStarted] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!authToken) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error(await parseApiError(res));
      const data = await res.json();
      const user = data?.user || {};
      setEnabled(Boolean(user.totp_enabled));
      setVerifiedAt(user.totp_verified_at || null);
    } catch (err) {
      setError(err.message || 'Failed to load 2FA status');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleEnable = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/admin/totp/setup`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: '{}',
      });
      if (!res.ok) throw new Error(await parseApiError(res));
      const data = await res.json();
      const url = data?.otpauth_url || '';
      setSecret(data?.secret || '');
      setSetupStarted(true);
      setToken('');
      if (url) {
        const qr = await QRCode.toDataURL(url, QR_OPTS);
        setQrDataUrl(qr);
      }
    } catch (err) {
      setError(err.message || 'Failed to start 2FA setup');
    } finally {
      setBusy(false);
    }
  };

  const handleVerifySetup = async () => {
    if (!/^\d{6}$/.test(token.trim())) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/admin/totp/verify-setup`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: token.trim() }),
      });
      if (!res.ok) throw new Error(await parseApiError(res));
      setEnabled(true);
      setSetupStarted(false);
      setSecret('');
      setQrDataUrl('');
      setToken('');
      setMessage('2FA Enabled');
      setVerifiedAt(new Date().toISOString());
    } catch (err) {
      setError(err.message || 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <p style={{ color: '#8fa1b7', fontSize: 13, margin: 0 }}>Loading security settings…</p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ color: '#8fa1b7', fontSize: 12, margin: 0, lineHeight: 1.45 }}>
        Protect admin and Finance Hub access with a time-based authenticator code
        (Google Authenticator or compatible app).
      </p>

      {enabled && !setupStarted && (
        <div
          style={{
            border: '1px solid rgba(76,175,80,0.45)',
            background: 'rgba(76,175,80,0.12)',
            color: '#4CAF50',
            borderRadius: 10,
            padding: '10px 12px',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          2FA Enabled ✅
          {verifiedAt && (
            <span style={{ display: 'block', marginTop: 4, fontWeight: 400, fontSize: 11, color: '#8fa1b7' }}>
              Verified {new Date(verifiedAt).toLocaleString('en-IN')}
            </span>
          )}
        </div>
      )}

      {!enabled && !setupStarted && (
        <Button type="button" onClick={handleEnable} disabled={busy || !authToken}>
          {busy ? 'Starting…' : 'Enable 2FA'}
        </Button>
      )}

      {setupStarted && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ color: '#c9a84c', fontSize: 13, margin: 0, fontWeight: 600 }}>
            Scan this QR code with Google Authenticator
          </p>
          {qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt="TOTP QR code"
              width={220}
              height={220}
              style={{ borderRadius: 12, border: '1px solid rgba(201,168,76,0.35)', alignSelf: 'flex-start' }}
            />
          ) : null}
          {secret && (
            <p style={{ color: '#8fa1b7', fontSize: 11, margin: 0, wordBreak: 'break-all' }}>
              Manual key: <span style={{ color: '#e2c97e', fontFamily: 'monospace' }}>{secret}</span>
            </p>
          )}
          <div style={{ maxWidth: 280 }}>
            <Label htmlFor="admin-totp-setup-token" className="normal-case tracking-normal text-de-muted">
              Enter verification code
            </Label>
            <Input
              id="admin-totp-setup-token"
              className="mt-1.5 h-11 tracking-[0.35em]"
              type="tel"
              inputMode="numeric"
              placeholder="000000"
              maxLength={6}
              value={token}
              onChange={(e) => {
                setToken(e.target.value.replace(/\D/g, '').slice(0, 6));
                setError('');
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleVerifySetup()}
              autoComplete="one-time-code"
            />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button type="button" onClick={handleVerifySetup} disabled={busy || token.length !== 6}>
              {busy ? 'Verifying…' : 'Verify & enable'}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => {
                setSetupStarted(false);
                setSecret('');
                setQrDataUrl('');
                setToken('');
                setError('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {message && !error && (
        <div
          style={{
            border: '1px solid rgba(76,175,80,0.45)',
            background: 'rgba(76,175,80,0.12)',
            color: '#4CAF50',
            borderRadius: 8,
            padding: '8px 10px',
            fontSize: 12,
          }}
        >
          {message}
        </div>
      )}
      {error && (
        <div
          style={{
            border: '1px solid rgba(233,69,96,0.45)',
            background: 'rgba(233,69,96,0.14)',
            color: '#ff9aa8',
            borderRadius: 8,
            padding: '8px 10px',
            fontSize: 12,
          }}
          role="alert"
        >
          {error}
        </div>
      )}
    </div>
  );
}
